import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Users, ShoppingCart, DollarSign, Shield, Ban, Check, ArrowUpCircle, Link2, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

// ============================================================
// DASHBOARD GLOBAL SUPER ADMIN
// ============================================================
export function SuperAdminDashboard() {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['sa-dashboard'],
    queryFn: async () => (await api.get('/superadmin/dashboard')).data,
  })

  if (isLoading) return <div className="text-ink-500">Carregando...</div>
  if (!data) return null

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Building2} label="Empresas Ativas" value={data.empresas_ativas} sub={`${data.total_empresas} total`} tone="brand" />
        <KPI icon={Users} label="Usuários" value={data.total_usuarios} tone="green" />
        <KPI icon={ShoppingCart} label="Pedidos (mês)" value={data.total_pedidos_mes} tone="amber" />
        <KPI icon={DollarSign} label="MRR" value={formatMoney(data.mrr)} tone="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">Distribuição por Plano</h3>
          <div className="space-y-2">
            {Object.entries(data.planos_distribuicao as Record<string, number>).map(([plano, qtd]) => (
              <div key={plano} className="flex items-center justify-between text-sm">
                <span className="capitalize font-medium">{plano}</span>
                <span className="text-ink-600">{qtd} empresas</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">Trials Vencidos</h3>
          {(data.trials_vencidos as any[]).length === 0 ? (
            <p className="text-sm text-ink-500">Nenhum trial vencido.</p>
          ) : (
            <div className="space-y-2">
              {(data.trials_vencidos as any[]).map((t: any) => (
                <div key={t.id} className="flex items-center justify-between text-sm bg-amber-50 rounded px-3 py-2">
                  <span className="font-medium">{t.nome}</span>
                  <span className="text-amber-700 text-xs">Expirou em {t.trial_ate}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card card-body">
        <h3 className="font-semibold text-ink-900 mb-3">Últimas Empresas Cadastradas</h3>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Empresa</th><th>Plano</th><th>Data</th></tr></thead>
            <tbody>
              {(data.empresas_recentes as any[]).map((e: any) => (
                <tr key={e.id}>
                  <td className="font-medium">{e.nome}</td>
                  <td><PlanoBadge plano={e.plano} /></td>
                  <td className="text-ink-600 text-xs">{new Date(e.criado_em).toLocaleDateString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// GESTÃO DE EMPRESAS
// ============================================================
export function SuperAdminEmpresas() {
  const qc = useQueryClient()
  const [filtroPlano, setFiltroPlano] = useState('')
  const { data: empresas = [], isLoading } = useQuery<any[]>({
    queryKey: ['sa-empresas', filtroPlano],
    queryFn: async () => (await api.get('/superadmin/empresas', { params: { plano: filtroPlano || undefined } })).data,
  })

  const bloquear = useMutation({
    mutationFn: async (id: number) => (await api.put(`/superadmin/empresas/${id}/bloquear`)).data,
    onSuccess: () => { toast.success('Empresa bloqueada'); qc.invalidateQueries({ queryKey: ['sa-empresas'] }) },
  })
  const ativar = useMutation({
    mutationFn: async (id: number) => (await api.put(`/superadmin/empresas/${id}/ativar`)).data,
    onSuccess: () => { toast.success('Empresa ativada'); qc.invalidateQueries({ queryKey: ['sa-empresas'] }) },
  })
  const alterarPlano = useMutation({
    mutationFn: async ({ id, plano }: { id: number; plano: string }) =>
      (await api.put(`/superadmin/empresas/${id}/plano`, { plano })).data,
    onSuccess: () => { toast.success('Plano alterado'); qc.invalidateQueries({ queryKey: ['sa-empresas'] }) },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select className="input max-w-xs" value={filtroPlano} onChange={e => setFiltroPlano(e.target.value)}>
          <option value="">Todos os planos</option>
          <option value="trial">Trial</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
          <option value="bloqueado">Bloqueado</option>
        </select>
        <span className="text-sm text-ink-500">{empresas.length} empresas</span>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th>Empresa</th><th>CNPJ</th><th>Plano</th>
            <th className="text-right">Usuários</th><th className="text-right">Pedidos</th>
            <th>Status</th><th className="w-48">Ações</th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-8 text-ink-500">Carregando...</td></tr>}
            {empresas.map((e: any) => (
              <tr key={e.id}>
                <td>
                  <div className="font-medium">{e.nome_fantasia}</div>
                  <div className="text-xs text-ink-500">{e.email}</div>
                </td>
                <td className="font-mono text-xs">{e.cnpj || '-'}</td>
                <td><PlanoBadge plano={e.plano} /></td>
                <td className="text-right">{e.total_usuarios}/{e.max_usuarios}</td>
                <td className="text-right">{e.total_pedidos_mes}</td>
                <td>{e.ativo ? <span className="badge-success">Ativo</span> : <span className="badge-danger">Bloqueado</span>}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    <select className="input !py-1 !px-2 text-xs w-24"
                      value={e.plano}
                      onChange={ev => alterarPlano.mutate({ id: e.id, plano: ev.target.value })}>
                      <option value="trial">Trial</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                    {e.ativo ? (
                      <button onClick={() => { if (confirm(`Bloquear ${e.nome_fantasia}?`)) bloquear.mutate(e.id) }}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Bloquear">
                        <Ban size={14} />
                      </button>
                    ) : (
                      <button onClick={() => ativar.mutate(e.id)}
                        className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="Ativar">
                        <Check size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================
// BILLING
// ============================================================
export function SuperAdminBilling() {
  const { data: billing = [], isLoading } = useQuery<any[]>({
    queryKey: ['sa-billing'],
    queryFn: async () => (await api.get('/superadmin/billing')).data,
  })
  const { data: planos } = useQuery<any>({
    queryKey: ['sa-planos'],
    queryFn: async () => (await api.get('/superadmin/planos')).data,
  })

  return (
    <div className="space-y-6">
      {/* Planos */}
      {planos && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(planos as Record<string, any>).map(([key, p]) => (
            <div key={key} className="card card-body text-center">
              <div className="text-sm font-semibold text-ink-900 mb-1">{p.nome}</div>
              <div className="text-2xl font-bold text-brand-600">
                {p.valor === 0 ? 'Grátis' : `R$ ${p.valor}/mês`}
              </div>
              <div className="text-xs text-ink-500 mt-2 space-y-1">
                <div>{p.max_usuarios} usuários</div>
                <div>{p.max_produtos.toLocaleString()} produtos</div>
                <div>{p.max_pedidos_mes.toLocaleString()} pedidos/mês</div>
                <div className="text-[10px] text-ink-400">{p.modulos.join(', ')}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assinaturas */}
      <div className="card">
        <div className="px-5 py-4 border-b border-ink-200">
          <h3 className="font-semibold">Assinaturas</h3>
        </div>
        <div className="table-wrap !rounded-t-none !border-t-0">
          <table className="table">
            <thead><tr>
              <th>Empresa</th><th>Plano</th><th className="text-right">Valor/mês</th>
              <th>Status</th><th>Próx. Vencimento</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="text-center py-8">Carregando...</td></tr>}
              {billing.length === 0 && !isLoading && <tr><td colSpan={5} className="text-center py-8 text-ink-500">Nenhuma assinatura.</td></tr>}
              {billing.map((b: any) => (
                <tr key={b.id}>
                  <td>Empresa #{b.empresa_id}</td>
                  <td><PlanoBadge plano={b.plano} /></td>
                  <td className="text-right font-medium">{formatMoney(b.valor_mensal)}</td>
                  <td><span className={b.status === 'ativo' ? 'badge-success' : b.status === 'cancelado' ? 'badge-danger' : 'badge-warning'}>{b.status}</span></td>
                  <td className="text-ink-600">{b.proximo_vencimento || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// CONFIGURAÇÃO DE IA
// ============================================================
export function IAConfigPage() {
  const { data: status } = useQuery<any>({
    queryKey: ['ia-status'],
    queryFn: async () => (await api.get('/ia/status')).data,
  })

  return (
    <div className="max-w-2xl space-y-4">
      <div className="card card-body">
        <h3 className="font-semibold text-ink-900 mb-3">Sofia</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-700">Status</span>
            {status?.configurado
              ? <span className="badge-success">Configurada</span>
              : <span className="badge-danger">Não configurada</span>}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-700">Provider</span>
            <span className="text-sm font-mono text-ink-600">{status?.provider || '-'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-700">Modelo</span>
            <span className="text-sm font-mono text-ink-600">{status?.modelo || '-'}</span>
          </div>
        </div>
        {!status?.configurado && (
          <div className="mt-4 p-3 bg-amber-50 rounded-md text-sm text-amber-800">
            Para ativar a Sofia, configure a variável <code className="bg-white px-1 rounded">AI_API_KEY</code> no arquivo <code className="bg-white px-1 rounded">.env</code> do backend com sua chave da Anthropic.
          </div>
        )}
      </div>

      <div className="card card-body">
        <h3 className="font-semibold text-ink-900 mb-2">O que a Sofia pode fazer</h3>
        <ul className="text-sm text-ink-700 space-y-1.5">
          <li className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Responder dúvidas sobre como usar o ERP</li>
          <li className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Analisar dados de vendas, estoque e financeiro em tempo real</li>
          <li className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Sugerir produtos para reposição de estoque</li>
          <li className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Gerar resumos financeiros e relatórios rápidos</li>
          <li className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Dar dicas de gestão empresarial</li>
        </ul>
      </div>
    </div>
  )
}

// ============================================================
// HELPERS
// ============================================================
function PlanoBadge({ plano }: { plano: string }) {
  const colors: Record<string, string> = {
    enterprise: 'bg-amber-100 text-amber-800 ring-amber-300',
    pro: 'bg-brand-50 text-brand-800 ring-brand-300',
    starter: 'bg-emerald-50 text-emerald-800 ring-emerald-300',
    trial: 'bg-ink-100 text-ink-700 ring-ink-300',
    bloqueado: 'bg-red-50 text-red-800 ring-red-300',
  }
  return <span className={`badge ${colors[plano] || 'badge-neutral'} capitalize`}>{plano}</span>
}

function KPI({ icon: Icon, label, value, sub, tone }: { icon: any; label: string; value: any; sub?: string; tone: string }) {
  const tones: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-600', green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600', red: 'bg-red-50 text-red-600',
  }
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-ink-500 uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-semibold text-ink-900 mt-2">{value}</div>
          {sub && <div className="text-xs text-ink-500 mt-1">{sub}</div>}
        </div>
        <div className={`p-2.5 rounded-lg ${tones[tone]}`}><Icon size={18} /></div>
      </div>
    </div>
  )
}


// ============================================================
// GRUPOS — ligações intercompany
// ============================================================

interface EmpresaLight { id: number; razao_social: string; nome_fantasia: string; cnpj: string | null; ativo: boolean }

export function SuperAdminGrupos() {
  const qc = useQueryClient()
  const [empA, setEmpA] = useState<number | ''>('')
  const [empB, setEmpB] = useState<number | ''>('')

  const { data: empresas = [] } = useQuery<EmpresaLight[]>({
    queryKey: ['sa-empresas'],
    queryFn: async () => (await api.get('/superadmin/empresas')).data,
  })

  const { data: ligacoes } = useQuery<{ ligacoes: any[] }>({
    queryKey: ['sa-ligacoes'],
    queryFn: async () => (await api.get('/intercompany/ligacoes')).data,
  })

  const ligar = useMutation({
    mutationFn: async () => (await api.post('/intercompany/ligar-empresas', {
      empresa_a_id: empA, empresa_b_id: empB,
    })).data,
    onSuccess: () => {
      toast.success('Empresas ligadas — cada uma já vê a outra como fornecedor/cliente')
      setEmpA(''); setEmpB('')
      qc.invalidateQueries({ queryKey: ['sa-ligacoes'] })
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="text-brand-600" />
        <div>
          <h1 className="text-2xl font-bold">Grupos / Intercompany</h1>
          <p className="text-sm text-ink-600">Ligue empresas do mesmo grupo para permitir pedido de compra de uma gerar pedido de venda na outra automaticamente.</p>
        </div>
      </div>

      <div className="card card-body">
        <h3 className="font-semibold mb-3">Ligar duas empresas</h3>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <label className="block">
            <div className="text-xs font-medium mb-1">Empresa A</div>
            <select className="input w-full" value={empA}
              onChange={e => setEmpA(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— selecione —</option>
              {empresas.filter(e => e.ativo).map(e => (
                <option key={e.id} value={e.id} disabled={e.id === empB}>{e.razao_social}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-medium mb-1">Empresa B</div>
            <select className="input w-full" value={empB}
              onChange={e => setEmpB(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— selecione —</option>
              {empresas.filter(e => e.ativo).map(e => (
                <option key={e.id} value={e.id} disabled={e.id === empA}>{e.razao_social}</option>
              ))}
            </select>
          </label>
          <button disabled={!empA || !empB || ligar.isPending}
            onClick={() => ligar.mutate()} className="btn-primary">
            {ligar.isPending && <Loader2 size={14} className="animate-spin" />}
            <Link2 size={14} /> Ligar
          </button>
        </div>
        <div className="text-xs text-ink-500 mt-2">
          Ao ligar: cria uma Pessoa em cada empresa representando a outra (tipo "ambos", CNPJ já preenchido).
          Operação é idempotente — pode rodar de novo sem duplicar.
        </div>
      </div>

      <div className="card card-body">
        <h3 className="font-semibold mb-3">Ligações ativas</h3>
        {(!ligacoes?.ligacoes || ligacoes.ligacoes.length === 0) && (
          <div className="text-center text-ink-500 py-6 text-sm">Nenhuma ligação configurada ainda.</div>
        )}
        <div className="space-y-2">
          {ligacoes?.ligacoes?.map((l, i) => (
            <div key={i} className="flex items-center gap-2 p-2 border rounded">
              <Building2 size={16} className="text-brand-600" />
              <strong>{l.empresa_a?.razao_social || '?'}</strong>
              <Link2 size={14} className="text-ink-400" />
              <strong>{l.empresa_b?.razao_social || '?'}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
